import type { FormEvent } from 'react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { AppShell } from '../components/AppShell';
import { EmptyState } from '../components/EmptyState';
import { PermissionGate } from '../components/PermissionGate';
import {
  Badge,
  Button,
  Card,
  Input,
  Modal,
  SectionHeader,
  Skeleton,
  Textarea,
  useActionDialog,
} from '../components/ui';
import { usePermissions, useRoles } from '../features/roles/hooks/useRoles';
import {
  useCreateRole,
  useDeleteRole,
  useUpdateRole,
} from '../features/roles/hooks/useRoleMutations';
import type { PermissionItem, Role, RoleCreatePayload, RoleUpdatePayload } from '../types/roles';
import styles from './RolesPage.module.scss';

export function RolesPage() {
  const { t } = useTranslation();

  return (
    <AppShell>
      <div className={styles.page}>
        <SectionHeader title={t('roles.title')} subtitle={t('roles.subtitle')} />
        <RolesTab />
      </div>
    </AppShell>
  );
}

function RolesTab() {
  const { t } = useTranslation();
  const { confirmAction } = useActionDialog();
  const { data: roles, isLoading, isError, error } = useRoles();
  const { data: permissions } = usePermissions();
  const deleteMutation = useDeleteRole();
  const [editingRole, setEditingRole] = useState<Role | null>(null);
  const [isCreateOpen, setIsCreateOpen] = useState(false);

  async function handleDeleteRole(role: Role) {
    const confirmed = await confirmAction({
      message: t('roles.deleteRoleConfirm', { name: role.name }),
      confirmLabel: t('dialog.delete'),
      tone: 'danger',
    });
    if (confirmed) deleteMutation.mutate(role.id);
  }

  if (isLoading) {
    return (
      <div className={styles.skeletonList}>
        {Array.from({ length: 3 }, (_, i) => (
          <Skeleton key={i} height={72} />
        ))}
      </div>
    );
  }

  if (isError) {
    return (
      <EmptyState
        title={t('roles.unableToLoadRoles')}
        description={error instanceof Error ? error.message : 'Try again in a moment.'}
      />
    );
  }

  return (
    <>
      <PermissionGate permission="roles:manage">
        <div className={styles.toolbar}>
          <Button variant="primary" onClick={() => setIsCreateOpen(true)}>
            {t('roles.createRoleBtn')}
          </Button>
        </div>
      </PermissionGate>

      {!roles || roles.length === 0 ? (
        <EmptyState title={t('roles.noRolesFound')} description={t('roles.noRolesDescription')} />
      ) : (
        <div className={styles.rolesList}>
          {roles.map((role) => (
            <Card key={role.id} padding="md">
              <div className={styles.roleRow}>
                <div className={styles.roleIdentity}>
                  <div className={styles.roleNameLine}>
                    <span className={styles.roleName}>{role.name}</span>
                    {role.is_system ? (
                      <Badge variant="accent" size="sm">
                        {t('roles.systemType')}
                      </Badge>
                    ) : (
                      <Badge variant="neutral" size="sm">
                        {t('roles.customType')}
                      </Badge>
                    )}
                  </div>
                  {role.description ? (
                    <p className={styles.roleDescription}>{role.description}</p>
                  ) : null}
                  <div className={styles.roleMeta}>
                    <span>{t('roles.permissionsCount', { count: role.permissions.length })}</span>
                  </div>
                </div>

                <div className={styles.roleActions}>
                  <PermissionGate permission="roles:manage">
                    <Button variant="ghost" size="sm" onClick={() => setEditingRole(role)}>
                      {t('roles.editBtn')}
                    </Button>
                    {!role.is_system ? (
                      <Button
                        variant="danger"
                        size="sm"
                        onClick={() => void handleDeleteRole(role)}
                      >
                        {t('roles.deleteBtn')}
                      </Button>
                    ) : null}
                  </PermissionGate>
                </div>
              </div>
            </Card>
          ))}
        </div>
      )}

      {isCreateOpen && permissions ? (
        <RoleFormModal
          title={t('roles.createRoleTitle')}
          description={t('roles.createRoleDescription')}
          permissions={permissions}
          onClose={() => setIsCreateOpen(false)}
          mode="create"
        />
      ) : null}

      {editingRole && permissions ? (
        <RoleFormModal
          title={t('roles.editRoleTitle', { name: editingRole.name })}
          description={t('roles.editRoleDescription')}
          permissions={permissions}
          role={editingRole}
          onClose={() => setEditingRole(null)}
          mode="edit"
        />
      ) : null}
    </>
  );
}

type RoleFormModalProps = {
  readonly title: string;
  readonly description: string;
  readonly permissions: readonly PermissionItem[];
  readonly role?: Role;
  readonly onClose: () => void;
  readonly mode: 'create' | 'edit';
};

function RoleFormModal({ title, description, permissions, role, onClose, mode }: RoleFormModalProps) {
  const { t } = useTranslation();
  const createMutation = useCreateRole();
  const updateMutation = useUpdateRole();
  const [name, setName] = useState(role?.name ?? '');
  const [desc, setDesc] = useState(role?.description ?? '');
  const [selectedPerms, setSelectedPerms] = useState<Set<string>>(
    new Set(role?.permissions ?? []),
  );

  const isSubmitting = createMutation.isPending || updateMutation.isPending;
  const errorMessage =
    createMutation.error instanceof Error
      ? createMutation.error.message
      : updateMutation.error instanceof Error
        ? updateMutation.error.message
        : null;

  function togglePerm(value: string) {
    setSelectedPerms((prev) => {
      const next = new Set(prev);
      if (next.has(value)) {
        next.delete(value);
      } else {
        next.add(value);
      }
      return next;
    });
  }

  async function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const perms = Array.from(selectedPerms);

    if (mode === 'create') {
      const payload: RoleCreatePayload = {
        name: name.trim(),
        description: desc.trim() || null,
        permissions: perms,
      };
      await createMutation.mutateAsync(payload);
    } else if (role) {
      const payload: RoleUpdatePayload = {
        ...(role.is_system ? {} : { name: name.trim() }),
        description: desc.trim() || null,
        permissions: perms,
      };
      await updateMutation.mutateAsync({ roleId: role.id, payload });
    }

    onClose();
  }

  const formId = 'role-form';

  return (
    <Modal
      open
      onClose={onClose}
      title={title}
      subtitle={description}
      size="md"
      footer={
        <>
          <Button type="button" variant="secondary" onClick={onClose} disabled={isSubmitting}>
            {t('roles.cancel')}
          </Button>
          <Button type="submit" form={formId} variant="primary" disabled={isSubmitting}>
            {isSubmitting
              ? t('roles.saving')
              : mode === 'create'
                ? t('roles.create')
                : t('roles.save')}
          </Button>
        </>
      }
    >
      <form id={formId} className={styles.form} onSubmit={handleSubmit}>
        <Input
          label={t('roles.labelName')}
          value={name}
          onChange={(e) => setName(e.target.value)}
          required
          disabled={mode === 'edit' && role?.is_system}
        />
        <Textarea
          label={t('roles.labelDescription')}
          value={desc}
          onChange={(e) => setDesc(e.target.value)}
          rows={3}
        />
        <div>
          <div style={{ marginBottom: 'calc(var(--sp, 4px) * 2)' }}>
            <span className={styles.secondaryText}>{t('roles.labelPermissions')}</span>
          </div>
          <div className={styles.permissionsGrid}>
            {permissions.map((perm) => (
              <label key={perm.value} className={styles.permCheckbox}>
                <input
                  type="checkbox"
                  checked={selectedPerms.has(perm.value)}
                  onChange={() => togglePerm(perm.value)}
                />
                {perm.label}
              </label>
            ))}
          </div>
        </div>
        {errorMessage ? <p className={styles.formError}>{errorMessage}</p> : null}
      </form>
    </Modal>
  );
}
